import { UserRole } from "@toorak/tc-common-fe-sdk";
import {
  isILP,
  isRole,
  isPrivilege,
  isAssetManagement,
  isDrawAMRole,
  isPurchasedLoansViewerOnly
} from "../../utils/AccessManagement";
import {
  InternalRolesEnum,
  DrawRolesEnum,
  AssetManagementRolesEnum
} from "../../utils/constants";

export interface HeaderTab {
  id: number;
  title: string;
  hide?: boolean;
}

const baseConnectTabs = (): HeaderTab[] => [
  { id: 0, title: "Dashboard" },
  { id: 1, title: "Loans" },
  { id: 2, title: "Purchase And Settlement" },
  { id: 3, title: "Documents" },
  { id: 4, title: "Scenario" }
];

const bulkUploadTabs = (): HeaderTab[] => [
  ...baseConnectTabs(),
  { id: 5, title: "Appraiser Search" },
  { id: 6, title: "Bulk Evaluation" }
];

const adminSupportTabs = (): HeaderTab[] => [
  ...baseConnectTabs(),
  { id: 5, title: "Appraiser" },
  { id: 6, title: "Bulk Evaluation" },
  { id: 7, title: "Admin Screen" }
];

const defaultConnectTabs = (): HeaderTab[] => {
  const tabs = [...baseConnectTabs(), { id: 5, title: "Appraiser Search" }];
  if (isRole("APPRAISER")) {
    tabs.push({ id: 9, title: "Toorval Appraisal" });
  }
  if (
    isRole(DrawRolesEnum.ORIGINATOR_DRAW) ||
    isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW)
  ) {
    tabs.push({ id: 8, title: "Draw Request" });
  }
  return tabs;
};

const appendOptionalConnectTabs = (tabs: HeaderTab[]) => {
  if (
    !isILP() &&
    !isAssetManagement() &&
    !isPurchasedLoansViewerOnly() &&
    (isRole(InternalRolesEnum.LAT_ADMIN) || isRole(UserRole.LAT_TREASURER))
  ) {
    tabs.push({ id: 10, title: "Financeability" });
    if (
      window.location.hostname === "qa.toorakcapital.info" ||
      window.location.hostname === "localhost"
    ) {
      tabs.push({ id: 6, title: "Docs Index Self Serve" });
    }
  }

  if (
    isPrivilege("publish_pricing") &&
    !isILP() &&
    !isAssetManagement() &&
    !isPurchasedLoansViewerOnly()
  ) {
    tabs.push({ id: 12, title: "Publish Pricing" });
  }

  if (
    isPrivilege("takeout_partner") &&
    !isILP() &&
    !isAssetManagement() &&
    !isPurchasedLoansViewerOnly()
  ) {
    tabs.push({ id: 13, title: "Take-out Partners" });
  }

  if (
    isRole(AssetManagementRolesEnum.PURCHASED_LOANS_VIEWER) &&
    !isPurchasedLoansViewerOnly()
  ) {
    tabs.push({ id: 14, title: "Purchased Loans" });
  }
};

const buildIlpTabs = (): HeaderTab[] => {
  const tabs: HeaderTab[] = [{ id: 0, title: "Loans" }];
  if (
    isRole(InternalRolesEnum.LAT_ADMIN) ||
    isRole(InternalRolesEnum.LAT_APPROVER)
  ) {
    tabs.push({ id: 2, title: "Approver Dashboard" });
  }
  if (isPrivilege("takeout_partner")) {
    tabs.push({ id: 6, title: "Take-out Partners" });
  }
  return tabs;
};

const buildAssetManagementTabs = (): HeaderTab[] => {
  const tabs: HeaderTab[] = [];
  if (
    isRole(AssetManagementRolesEnum.AM_SO) ||
    isPrivilege("view_asset_management")
  ) {
    tabs.splice(0, 0, { id: 1, title: "Servicer Onboarding" });
  }
  if (isDrawAMRole() || isPrivilege("view_asset_management")) {
    tabs.splice(0, 0, { id: 0, title: "Draw Request" });
    tabs.splice(2, 0, { id: 2, title: "Purchased Loans" });
  } else if (isRole(AssetManagementRolesEnum.PURCHASED_LOANS_VIEWER)) {
    tabs.push({ id: 2, title: "Purchased Loans" });
  }
  tabs.push({ id: 14, title: "Counsel Data" });
  if (isDrawAMRole()) {
    tabs.push({ id: 15, title: "Budget Review" });
  }
  return tabs;
};

const resolveBaseTabs = (
  roles: any,
  onDefaultTab?: (tabId: number) => void
): HeaderTab[] => {
  if (roles === undefined || roles?.length === 0) {
    onDefaultTab?.(3);
    return [{ id: 3, title: "Documents" }];
  }
  if (isPurchasedLoansViewerOnly()) {
    onDefaultTab?.(14);
    return [{ id: 14, title: "Purchased Loans" }];
  }
  const roleName = roles?.filter((item: any) => item === "LAT_BULK_UPLOAD");
  if (roleName?.[0] === "LAT_BULK_UPLOAD") {
    return bulkUploadTabs();
  }
  const adminSupport = roles?.filter((item: any) => item === "ADMIN_SUPPORT");
  if (adminSupport?.[0] === "ADMIN_SUPPORT") {
    return adminSupportTabs();
  }
  return defaultConnectTabs();
};

/**
 * Builds header navigation tabs for the current user/role/path context.
 */
export const buildHeaderTabs = ({
  roles,
  showTab,
  isAMRoleDrawDetails,
  onDefaultTab
}: {
  roles: any;
  showTab?: boolean;
  isAMRoleDrawDetails: boolean;
  onDefaultTab?: (tabId: number) => void;
}): HeaderTab[] => {
  if (showTab === false) {
    return [];
  }

  let tabs = resolveBaseTabs(roles, onDefaultTab);
  appendOptionalConnectTabs(tabs);

  if (isILP()) {
    tabs = buildIlpTabs();
  }

  if (
    (isAssetManagement() || isAMRoleDrawDetails) &&
    !isPurchasedLoansViewerOnly()
  ) {
    tabs = buildAssetManagementTabs();
  }

  return tabs;
};

export const getHeaderMinTabs = (isAMRoleDrawDetails: boolean): number =>
  ((window.location.hostname === "www.gcp.toorakcapital.info" ||
    window.location.hostname === "www.toorakcapital.info") &&
    !isRole(AssetManagementRolesEnum.AM_SO)) ||
  !(isAssetManagement() || isAMRoleDrawDetails)
    ? 3
    : 5;

export const getUserInitials = (
  firstName: string,
  lastName: string
): string => {
  let initials = "";
  if (firstName !== null && firstName !== "null" && firstName !== "") {
    initials += firstName.charAt(0);
  }
  if (lastName !== null && lastName !== "null" && lastName !== "") {
    initials += lastName.charAt(0);
  }
  return initials;
};

export const getPlatformName = (clickStatus: {
  toorakConnect?: boolean;
  internalProcessing?: boolean;
  assetManagement?: boolean;
}): string => {
  if (clickStatus.toorakConnect) return "Toorak Connect";
  if (clickStatus.internalProcessing) return "Internal Loan Processing";
  if (clickStatus.assetManagement) return "Asset Management";
  return "Notification Settings";
};
